import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, DocumentTextIcon, TruckIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import OrderTimeline from '../components/OrderTimeline';
import { fetchB2bOrderDetail, transitionB2bOrderStatus } from '../api/marketplaceApi';

type Tab = 'summary' | 'items' | 'suppliers' | 'fulfilment' | 'delivery' | 'payments' | 'activity';

const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'items', label: 'Items' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'fulfilment', label: 'Fulfilment' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'payments', label: 'Payments & Invoice' },
  { key: 'activity', label: 'Activity' },
];

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [supplierOrders, setSupplierOrders] = useState<any[]>([]);
  const [allowedTransitions, setAllowedTransitions] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('summary');

  const loadOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetchB2bOrderDetail(parseInt(id));
      setOrder(res.order);
      setItems(res.items || []);
      setSupplierOrders(res.supplierOrders || []);
      setAllowedTransitions(res.allowedTransitions || []);
      setEvents(res.events || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  const handleTransition = async (nextState: string) => {
    if (!id) return;
    try {
      await transitionB2bOrderStatus(parseInt(id), nextState);
      loadOrder();
    } catch (err: any) {
      alert(err.message || 'Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Order not found</p>
        <button onClick={() => navigate('/marketplace/buyer/orders')} className="mt-3 text-sm text-emerald-600 hover:underline">
          Back to Orders
        </button>
      </div>
    );
  }

  const timeline = [
    { status: 'draft', label: 'Order created', timestamp: order.created_at },
    ...(order.status !== 'draft' ? [
      { status: order.status, label: `Status: ${order.status.replace(/_/g, ' ')}`, timestamp: order.updated_at },
    ] : []),
    ...events.map((e: any) => ({
      status: e.next_state || e.event_type,
      label: e.event_type?.replace(/_/g, ' ') || e.description || '',
      timestamp: e.created_at,
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Back link */}
      <button
        onClick={() => navigate('/marketplace/buyer/orders')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="w-4 h-4" /> Back to Orders
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">{order.order_number}</h2>
            <StatusBadge status={order.status} />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {order.buyer_org_name || 'Your organisation'} · Created {new Date(order.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-2xl font-extrabold text-gray-900">
              <Money amount={order.total_amount} currency={order.currency} />
            </p>
            <p className="text-xs text-gray-400">{items.length} items · {supplierOrders.length} supplier{supplierOrders.length !== 1 ? 's' : ''}</p>
          </div>
          {allowedTransitions.length > 0 && (
            <div className="flex gap-2">
              {allowedTransitions.map((t: any) => (
                <button
                  key={t.state}
                  onClick={() => handleTransition(t.state)}
                  className="px-3 py-1.5 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Order Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Buyer</p>
                    <p className="font-medium">{order.buyer_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Organisation</p>
                    <p className="font-medium">{order.buyer_org_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Delivery Address</p>
                    <p className="font-medium">{order.delivery_address || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Requested Delivery</p>
                    <p className="font-medium">{order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Payment Terms</p>
                    <p className="font-medium">{order.payment_terms || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Notes</p>
                    <p className="font-medium">{order.notes || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Subtotal</p>
                    <p className="font-medium"><Money amount={order.subtotal} currency={order.currency} /></p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total</p>
                    <p className="font-bold text-lg"><Money amount={order.total_amount} currency={order.currency} /></p>
                  </div>
                  <div>
                    <p className="text-gray-500">Created</p>
                    <p className="font-medium">{new Date(order.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Updated</p>
                    <p className="font-medium">{new Date(order.updated_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Items Tab */}
          {activeTab === 'items' && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Product</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Grade</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Quantity</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Unit Price</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Supplier</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {items.map((item: any) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
                            {item.image_url ? (
                              <img src={item.image_url} alt="" className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <span className="text-emerald-300 text-lg">🌿</span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{item.title}</p>
                            {item.catalog_name && <p className="text-xs text-gray-400">{item.catalog_name}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{item.grade || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {item.commercial_quantity} {item.commercial_unit}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <Money amount={item.unit_price} currency={item.currency} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {item.supplier_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        <Money amount={item.total_price} currency={item.currency} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                  <tr>
                    <td colSpan={5} className="px-4 py-3 font-bold text-gray-900">Total</td>
                    <td className="px-4 py-3 text-right font-bold text-lg">
                      <Money amount={order.total_amount} currency={order.currency} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Suppliers Tab */}
          {activeTab === 'suppliers' && (
            <div className="space-y-4">
              {supplierOrders.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                  <p className="text-gray-400">No supplier orders</p>
                </div>
              ) : (
                supplierOrders.map((so: any) => (
                  <div key={so.id} className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{so.order_number}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{so.supplier_name || `Tenant #${so.supplier_tenant_id}`}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <StatusBadge status={so.status} size="sm" />
                          <span className="text-xs text-gray-400">
                            {so.items?.length || 0} items
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">
                          <Money amount={so.total_amount} currency={so.currency} />
                        </p>
                        {so.commission_amount > 0 && (
                          <p className="text-xs text-gray-400">Commission: <Money amount={so.commission_amount} currency={so.currency} /></p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Fulfilment Tab */}
          {activeTab === 'fulfilment' && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Fulfilment Status</h3>
              {supplierOrders.length === 0 ? (
                <p className="text-sm text-gray-400">No supplier orders to track</p>
              ) : (
                <div className="space-y-3">
                  {supplierOrders.map((so: any) => (
                    <div key={so.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{so.order_number}</p>
                        <p className="text-xs text-gray-500">{so.supplier_name}</p>
                      </div>
                      <StatusBadge status={so.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Delivery Tab */}
          {activeTab === 'delivery' && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Delivery Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Delivery Address</p>
                  <p className="font-medium">{order.delivery_address || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Requested Delivery Date</p>
                  <p className="font-medium">{order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '—'}</p>
                </div>
              </div>
              {supplierOrders.some((so: any) => ['in_transit', 'delivered'].includes(so.status)) ? (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-2">Shipment Tracking</p>
                  {supplierOrders.filter((so: any) => ['in_transit', 'delivered'].includes(so.status)).map((so: any) => (
                    <div key={so.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-2">
                      <TruckIcon className="w-5 h-5 text-indigo-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{so.order_number}</p>
                        <p className="text-xs text-gray-500">{so.supplier_name}</p>
                      </div>
                      <StatusBadge status={so.status} size="sm" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-gray-400">No active shipments yet</p>
              )}
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Payments & Invoice</h3>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <p className="text-gray-500">Payment Terms</p>
                  <p className="font-medium">{order.payment_terms || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Order Total</p>
                  <p className="font-bold text-lg"><Money amount={order.total_amount} currency={order.currency} /></p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500 mb-2">Invoice</p>
                {order.invoice_id ? (
                  <button className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-medium rounded-lg hover:bg-emerald-100">
                    <DocumentTextIcon className="w-4 h-4" />
                    Download Invoice PDF
                  </button>
                ) : (
                  <p className="text-sm text-gray-400">Invoice will be generated after order confirmation</p>
                )}
              </div>
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Order Activity</h3>
              <OrderTimeline events={timeline} currentStatus={order.status} />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Quick Info Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Info</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <StatusBadge status={order.status} size="sm" />
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Items</span>
                <span>{items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Suppliers</span>
                <span>{supplierOrders.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span className="font-bold"><Money amount={order.total_amount} currency={order.currency} /></span>
              </div>
            </div>
          </div>

          {/* Actions Card */}
          {allowedTransitions.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
              <div className="space-y-2">
                {allowedTransitions.map((t: any) => (
                  <button
                    key={t.state}
                    onClick={() => handleTransition(t.state)}
                    className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Timeline Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Timeline</h3>
            <OrderTimeline events={timeline} currentStatus={order.status} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
